/**
 * Toast Bridge Service
 * 
 * This service acts as a bridge between non-Vue contexts (services, utilities)
 * and the Vue toast system. It uses a simple pub/sub pattern to communicate
 * with the ToastProvider component.
 */

class ToastBridge {
  constructor() {
    this.listeners = []
  }

  /**
   * Subscribe to toast events
   * @param {Function} callback - Function to call when a toast event occurs
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.push(callback)
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  /**
   * Emit a toast event to all listeners
   * @private
   */
  emit(type, message, options = {}) {
    this.listeners.forEach(listener => {
      try {
        listener({ type, message, options })
      } catch (error) {
        console.error('Error in toast listener:', error)
      }
    })
  }

  /**
   * Show a generic toast
   */
  show(message, options = {}) {
    this.emit('show', message, options)
  }

  /**
   * Show a success toast
   */
  success(message, options = {}) {
    this.emit('success', message, options)
  }

  /**
   * Show an error toast
   */
  error(message, options = {}) {
    this.emit('error', message, options)
  }

  /**
   * Show a warning toast
   */
  warning(message, options = {}) {
    this.emit('warning', message, options)
  }

  /**
   * Show an info toast
   */
  info(message, options = {}) {
    this.emit('info', message, options)
  }

  /**
   * Show a primary toast
   */
  primary(message, options = {}) {
    this.emit('primary', message, options)
  }

  /**
   * Show a persistent toast
   */
  persistent(message, type = 'info') {
    this.emit('persistent', message, { type })
  }

  /**
   * Dismiss a specific toast
   */
  dismiss(id) {
    this.emit('dismiss', id)
  }

  /**
   * Clear all toasts
   */
  clearAll() {
    this.emit('clearAll')
  }
}

// Export singleton instance
export const toastBridge = new ToastBridge()

// Also export the class for testing purposes